// Phase 2: Admin Comparison Engine dashboard.
//
// Lets admins run the deduplication + ranking engine for any module and inspect:
//   - stat cards (offers fetched, canonical, duplicates merged, dedupe rate)
//   - supplier latency + error chips
//   - expandable canonical-item rows with all ranked offers side-by-side
//   - merge confidence + reason badges (exact fingerprint / fuzzy / single)
//   - ranking explanation badges (cheapest / margin / fastest / preferred)
//   - per-supplier aggregated analytics (success rate, win rate, avg latency, margin)
//   - recent comparison history
//
// Admin-only. Never wired into any customer-facing flow.
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Play, RefreshCw, ChevronDown, ChevronRight, Trophy, AlertTriangle, Gauge, Zap,
  Activity, Database, Layers, TrendingUp, Clock, Settings2, Info, History,
} from 'lucide-react';
import { api, formatApiErrorDetail } from '../../lib/api';
import { StatCard } from './partnersPrimitives';

// ─── Helpers ─────────────────────────────────────────────────────────────
const POLICIES = [
  { value: 'cheapest',           label: 'Cheapest',            hint: 'Lowest display price wins' },
  { value: 'highest_margin',     label: 'Highest Margin',      hint: 'Maximise platform margin' },
  { value: 'fastest_api',        label: 'Fastest API',         hint: 'Lowest supplier latency wins' },
  { value: 'preferred_supplier', label: 'Preferred Supplier',  hint: 'Specified supplier wins, else cheapest' },
];

const fmt = (n, currency = 'INR') =>
  Number.isFinite(n) ? `${currency === 'INR' ? '₹' : currency + ' '}${Math.round(n).toLocaleString()}` : '—';
const pct = (n) => (Number.isFinite(n) ? `${(n * 100).toFixed(1)}%` : '—');

const MERGE_REASON_BADGE = {
  exact_fingerprint: { label: 'Exact match',   className: 'bg-green-100 text-green-800 border-green-300' },
  fuzzy_match:       { label: 'Fuzzy match',   className: 'bg-amber-100 text-amber-800 border-amber-300' },
  single_supplier:   { label: 'Single source', className: 'bg-gray-100 text-gray-700 border-gray-300' },
};

const RANK_REASON_BADGE = {
  lowest_display_price:       'Cheapest',
  highest_margin:             'Highest margin',
  fastest_supplier_response:  'Fastest API',
  preferred_supplier:         'Preferred',
  secondary:                  '',
  unranked:                   '',
};

// ─── Small bits ──────────────────────────────────────────────────────────
const Pill = ({ children, className = '' }) => (
  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${className}`}>
    {children}
  </span>
);

const LatencyBar = ({ ms, max }) => {
  const pctW = max > 0 ? Math.min(100, Math.round((ms / max) * 100)) : 0;
  const color = ms < 800 ? 'bg-green-500' : ms < 2000 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2 min-w-[160px]">
      <div className="h-1.5 flex-1 rounded-full bg-gray-200 overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pctW}%` }} />
      </div>
      <span className="text-[11px] font-mono text-gray-600 w-14 text-right">{ms} ms</span>
    </div>
  );
};

// ─── Main page ───────────────────────────────────────────────────────────
const AdminComparison = () => {
  const [meta, setMeta] = useState(null);
  const [module, setModule] = useState('hotels');
  const [policy, setPolicy] = useState('cheapest');
  const [preferredSupplier, setPreferredSupplier] = useState('');
  const [useCache, setUseCache] = useState(false);
  const [includeMock, setIncludeMock] = useState(true);
  const [query, setQuery] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openItems, setOpenItems] = useState({});

  const [metrics, setMetrics] = useState([]);
  const [history, setHistory] = useState([]);

  // ── Bootstrap meta + reset query on module change ────────────────────
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/admin/comparison/meta');
        setMeta(r.data);
        setQuery(r.data.example_queries?.hotels || {});
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || 'Failed to load comparison meta');
      }
    })();
    loadAnalytics();
  }, []);

  useEffect(() => {
    if (meta?.example_queries?.[module]) {
      setQuery(meta.example_queries[module]);
      setResult(null);
      setOpenItems({});
    }
  }, [module, meta]);

  const loadAnalytics = async (mod) => {
    try {
      const params = mod ? { module: mod } : {};
      const [m, h] = await Promise.all([
        api.get('/admin/comparison/suppliers/metrics', { params }),
        api.get('/admin/comparison/history', { params: { ...params, limit: 20 } }),
      ]);
      setMetrics(m.data.items || []);
      setHistory(h.data.items || []);
    } catch (e) {
      // Non-fatal; analytics panel just stays empty
    }
  };

  const runCompare = async () => {
    setLoading(true);
    setResult(null);
    try {
      const body = {
        module,
        query,
        policy,
        preferred_supplier: policy === 'preferred_supplier' ? preferredSupplier || null : null,
        include_synthetic_mock: includeMock,
        use_cache: useCache,
        timeout_per_supplier: 15,
      };
      const r = await api.post('/admin/comparison/run', body);
      setResult(r.data);
      toast.success(`Engine returned ${r.data.items?.length || 0} canonical items in ${Math.max(...Object.values(r.data.stats?.latencies || { 0: 0 }))} ms`);
      loadAnalytics(module);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || 'Comparison failed');
    } finally {
      setLoading(false);
    }
  };

  const clearCache = async () => {
    try {
      await api.post('/admin/comparison/cache/clear');
      toast.success('Comparison cache cleared');
    } catch {
      toast.error('Failed to clear cache');
    }
  };

  const maxLatency = useMemo(() => {
    const all = Object.values(result?.stats?.latencies || {});
    return all.length ? Math.max(...all, 1) : 1;
  }, [result]);

  const toggleItem = (id) => setOpenItems((s) => ({ ...s, [id]: !s[id] }));

  const stats = result?.stats || {};

  // ── UI ──────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6" data-testid="admin-comparison-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Layers className="h-7 w-7 text-red-600" />
            Comparison Engine
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Phase 2 · Fan-out search across active suppliers, deduplicate inventory, rank by policy.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={clearCache}
            data-testid="comparison-clear-cache-btn"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <RefreshCw className="h-4 w-4" />
            Clear Cache
          </button>
        </div>
      </div>

      {/* Query builder */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 space-y-4" data-testid="comparison-query-builder">
        <div className="flex items-center gap-2">
          <Settings2 className="h-4 w-4 text-gray-500" />
          <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Run Configuration</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Module */}
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-wider font-semibold text-gray-500">Module</span>
            <select
              value={module}
              onChange={(e) => setModule(e.target.value)}
              data-testid="comparison-module-select"
              className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2 text-sm text-gray-900 dark:text-white"
            >
              {(meta?.modules || ['hotels']).map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </label>

          {/* Policy */}
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-wider font-semibold text-gray-500">Ranking Policy</span>
            <select
              value={policy}
              onChange={(e) => setPolicy(e.target.value)}
              data-testid="comparison-policy-select"
              className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2 text-sm text-gray-900 dark:text-white"
            >
              {POLICIES.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </label>

          {/* Preferred supplier (only if policy needs it) */}
          {policy === 'preferred_supplier' && (
            <label className="flex flex-col gap-1">
              <span className="text-xs uppercase tracking-wider font-semibold text-gray-500">Preferred Supplier ID</span>
              <input
                value={preferredSupplier}
                onChange={(e) => setPreferredSupplier(e.target.value)}
                data-testid="comparison-preferred-input"
                placeholder="e.g. TP002"
                className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2 text-sm text-gray-900 dark:text-white"
              />
            </label>
          )}

          {/* Toggles */}
          <div className="flex items-end gap-4 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={includeMock}
                onChange={(e) => setIncludeMock(e.target.checked)}
                data-testid="comparison-include-mock-toggle"
                className="h-4 w-4 accent-red-600"
              />
              Include platform mock supplier
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={useCache}
                onChange={(e) => setUseCache(e.target.checked)}
                data-testid="comparison-use-cache-toggle"
                className="h-4 w-4 accent-red-600"
              />
              Use cached response
            </label>
          </div>
        </div>

        {/* Query fields — JSON-style editable inputs from example query */}
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="comparison-query-fields">
          {Object.entries(query).map(([k, v]) => (
            <label key={k} className="flex flex-col gap-1 min-w-0">
              <span className="text-xs uppercase tracking-wider font-semibold text-gray-500">{k}</span>
              <input
                value={v ?? ''}
                onChange={(e) => setQuery((q) => ({ ...q, [k]: e.target.value }))}
                data-testid={`comparison-query-${k}`}
                className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2 text-sm text-gray-900 dark:text-white"
              />
            </label>
          ))}
        </div>

        <div className="flex justify-end">
          <button
            onClick={runCompare}
            disabled={loading}
            data-testid="comparison-run-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white px-5 py-2.5 text-sm font-semibold shadow"
          >
            {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Run Comparison
          </button>
        </div>
      </div>

      {/* Stat cards */}
      {result && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="comparison-stats">
          <StatCard label="Offers fetched" value={stats.total_offers_fetched ?? 0} accent="blue" testId="stat-offers-fetched" />
          <StatCard label="Canonical items" value={stats.canonical_items ?? 0} accent="gray" testId="stat-canonical-items" />
          <StatCard label="Duplicates merged" value={stats.duplicates_merged ?? 0} accent="amber" testId="stat-duplicates-merged" />
          <StatCard label="Dedupe rate" value={pct(stats.dedupe_rate)} accent="red" testId="stat-dedupe-rate" />
          <StatCard label="Errors" value={(result.errors || []).length} accent={(result.errors || []).length ? 'red' : 'green'} testId="stat-errors" />
        </div>
      )}

      {/* Supplier latency bars */}
      {result && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5" data-testid="comparison-suppliers-panel">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="h-4 w-4 text-gray-500" />
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Supplier Latency · This Run</p>
          </div>
          <div className="space-y-2">
            {(result.supplier_runs || []).map((s) => (
              <div key={s.supplier_id} className="flex items-center gap-3 flex-wrap" data-testid={`supplier-run-${s.supplier_id}`}>
                <span className="font-mono text-xs text-gray-500 w-24 truncate">{s.supplier_id}</span>
                <span className="font-semibold text-sm text-gray-800 dark:text-gray-200 w-32 truncate">{s.supplier_name}</span>
                <LatencyBar ms={s.latency_ms} max={maxLatency} />
                <Pill className={s.ok ? 'bg-green-100 text-green-800 border-green-300' : 'bg-red-100 text-red-800 border-red-300'}>
                  {s.ok ? `${s.items_count} items` : 'Failed'}
                </Pill>
                {s.error && (
                  <span className="text-[11px] text-red-600 font-medium flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> {s.error}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Canonical items */}
      {result && (result.items?.length ?? 0) > 0 && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden" data-testid="comparison-items-panel">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-gray-500" />
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Canonical Inventory</p>
              <span className="text-xs text-gray-500">{result.items.length} unique products · ranked by <strong>{policy}</strong></span>
            </div>
          </div>
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {result.items.slice(0, 50).map((it) => {
              const open = !!openItems[it.canonical_id];
              const mr = MERGE_REASON_BADGE[it.merge_reason] || MERGE_REASON_BADGE.single_supplier;
              return (
                <li key={it.canonical_id} data-testid={`canonical-item-${it.canonical_id}`}>
                  <button
                    type="button"
                    onClick={() => toggleItem(it.canonical_id)}
                    className="w-full flex items-center gap-3 px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 text-left"
                    data-testid={`canonical-toggle-${it.canonical_id}`}
                  >
                    {open ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-gray-900 dark:text-white truncate">{it.name || '(unnamed)'}</p>
                        <Pill className={mr.className}>
                          {mr.label} · {Math.round((it.merge_confidence || 1) * 100)}%
                        </Pill>
                        <Pill className="bg-gray-50 text-gray-700 border-gray-200">
                          {it.supplier_count} {it.supplier_count === 1 ? 'supplier' : 'suppliers'}
                        </Pill>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 font-mono truncate">{it.canonical_id}</p>
                    </div>
                    {it.winner && (
                      <div className="flex items-center gap-2 shrink-0">
                        <Trophy className="h-4 w-4 text-amber-500" />
                        <div className="text-right">
                          <p className="text-[11px] text-gray-500 uppercase tracking-wider">Winner</p>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {fmt(it.winner.display_price, it.winner.currency)}
                            <span className="ml-1 text-xs font-normal text-gray-500">· {it.winner.supplier_name}</span>
                          </p>
                        </div>
                      </div>
                    )}
                  </button>

                  {open && (
                    <div className="bg-gray-50 dark:bg-gray-950/50 px-5 py-4 border-t border-gray-100 dark:border-gray-800">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-[11px] uppercase tracking-wider text-gray-500">
                              <th className="text-left font-semibold py-2">Rank</th>
                              <th className="text-left font-semibold py-2">Supplier</th>
                              <th className="text-right font-semibold py-2">Net</th>
                              <th className="text-right font-semibold py-2">Selling</th>
                              <th className="text-right font-semibold py-2">Display</th>
                              <th className="text-right font-semibold py-2">Margin</th>
                              <th className="text-right font-semibold py-2">Margin %</th>
                              <th className="text-right font-semibold py-2">Latency</th>
                              <th className="text-left font-semibold py-2 pl-3">Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(it.offers || []).map((o, i) => (
                              <tr key={`${o.supplier_id}-${i}`} className={i === 0 ? 'bg-amber-50 dark:bg-amber-900/10' : ''} data-testid={`offer-row-${o.supplier_id}-${i}`}>
                                <td className="py-2 pr-3 font-mono text-gray-500">#{o.rank}</td>
                                <td className="py-2 pr-3 font-medium text-gray-800 dark:text-gray-200">
                                  {i === 0 && <Trophy className="inline h-3 w-3 text-amber-500 mr-1" />}
                                  {o.supplier_name}
                                  <span className="ml-2 text-xs text-gray-400 font-mono">{o.supplier_id}</span>
                                </td>
                                <td className="py-2 pr-3 text-right tabular-nums text-gray-600">{fmt(o.net_price, o.currency)}</td>
                                <td className="py-2 pr-3 text-right tabular-nums text-gray-600">{fmt(o.selling_price, o.currency)}</td>
                                <td className="py-2 pr-3 text-right tabular-nums font-semibold text-gray-900 dark:text-white">{fmt(o.display_price, o.currency)}</td>
                                <td className="py-2 pr-3 text-right tabular-nums text-green-700">{fmt(o.margin_absolute, o.currency)}</td>
                                <td className="py-2 pr-3 text-right tabular-nums text-green-700">{pct(o.margin_pct)}</td>
                                <td className="py-2 pr-3 text-right tabular-nums text-gray-500">{o.latency_ms ?? '—'} ms</td>
                                <td className="py-2 pl-3">
                                  {i === 0 ? (
                                    <Pill className="bg-amber-100 text-amber-900 border-amber-300">
                                      {RANK_REASON_BADGE[o.rank_reason] || 'Winner'}
                                    </Pill>
                                  ) : (
                                    <span className="text-xs text-gray-400">runner-up</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          {result.items.length > 50 && (
            <div className="px-5 py-3 text-xs text-gray-500 bg-gray-50 dark:bg-gray-950/50 border-t border-gray-100 dark:border-gray-800">
              Showing first 50 of {result.items.length} canonical items.
            </div>
          )}
        </div>
      )}

      {/* Errors panel */}
      {result?.errors?.length > 0 && (
        <div className="rounded-xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 p-4" data-testid="comparison-errors-panel">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <p className="text-sm font-semibold text-red-700 dark:text-red-300">Supplier Errors</p>
          </div>
          <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
            {result.errors.map((e) => (
              <li key={e.supplier_id} className="flex items-center gap-2">
                <span className="font-mono text-xs">{e.supplier_id}</span>
                <span className="font-semibold">{e.supplier_name}</span>
                <span>·</span>
                <span>{e.error}</span>
                <span className="ml-auto text-xs">{e.latency_ms} ms</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Supplier metrics analytics */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5" data-testid="comparison-metrics-panel">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="h-4 w-4 text-gray-500" />
          <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Supplier Performance (aggregated across runs)</p>
        </div>
        {metrics.length === 0 ? (
          <p className="text-sm text-gray-500">No data yet. Run a comparison to populate metrics.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="text-left py-2">Supplier</th>
                  <th className="text-left py-2">Module</th>
                  <th className="text-right py-2">Runs</th>
                  <th className="text-right py-2">Success</th>
                  <th className="text-right py-2">Win Rate</th>
                  <th className="text-right py-2">Avg Latency</th>
                  <th className="text-right py-2">Margin (₹)</th>
                </tr>
              </thead>
              <tbody>
                {metrics.map((m) => (
                  <tr key={`${m.supplier_id}-${m.module}`} data-testid={`metric-row-${m.supplier_id}`}>
                    <td className="py-2">
                      <span className="font-semibold text-gray-800 dark:text-gray-200">{m.supplier_name}</span>
                      <span className="ml-2 text-xs text-gray-400 font-mono">{m.supplier_id}</span>
                    </td>
                    <td className="py-2 text-gray-600 dark:text-gray-400">{m.module}</td>
                    <td className="py-2 text-right tabular-nums">{m.runs}</td>
                    <td className="py-2 text-right tabular-nums">{pct(m.success_rate)}</td>
                    <td className="py-2 text-right tabular-nums">{pct(m.win_rate)}</td>
                    <td className="py-2 text-right tabular-nums">{m.avg_latency_ms} ms</td>
                    <td className="py-2 text-right tabular-nums text-green-700">₹{Math.round(m.total_margin_contribution || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* History */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5" data-testid="comparison-history-panel">
        <div className="flex items-center gap-2 mb-3">
          <History className="h-4 w-4 text-gray-500" />
          <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Recent Runs</p>
        </div>
        {history.length === 0 ? (
          <p className="text-sm text-gray-500">No history yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800 text-sm">
            {history.map((h) => (
              <li key={h.comparison_id + h.timestamp} className="py-2 flex items-center gap-3 flex-wrap" data-testid={`history-row-${h.comparison_id}`}>
                <Clock className="h-3 w-3 text-gray-400" />
                <span className="text-xs text-gray-500">{new Date(h.timestamp).toLocaleString()}</span>
                <Pill className="bg-gray-100 text-gray-700 border-gray-200">{h.module}</Pill>
                <Pill className="bg-blue-50 text-blue-700 border-blue-200">{h.policy}</Pill>
                <span className="text-xs text-gray-600">
                  <Gauge className="inline h-3 w-3 mr-1 text-gray-400" />
                  {h.stats?.canonical_items ?? 0} items
                </span>
                <span className="text-xs text-gray-600">
                  <Zap className="inline h-3 w-3 mr-1 text-gray-400" />
                  dedupe {pct(h.stats?.dedupe_rate)}
                </span>
                {h.errors?.length > 0 && (
                  <Pill className="bg-red-100 text-red-800 border-red-300">{h.errors.length} errors</Pill>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Ranking + merge docs */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950/30 p-5 text-xs text-gray-600 dark:text-gray-400" data-testid="comparison-docs-panel">
        <div className="flex items-center gap-2 mb-2">
          <Info className="h-4 w-4 text-gray-500" />
          <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">How it works</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <p className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Merge confidence</p>
            <ul className="space-y-1 list-disc list-inside">
              {meta && Object.entries(meta.merge_confidence_doc || {}).map(([k, v]) => (
                <li key={k}><strong>{k}</strong>: {v}</li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Ranking policies</p>
            <ul className="space-y-1 list-disc list-inside">
              {meta && Object.entries(meta.ranking_doc || {}).map(([k, v]) => (
                <li key={k}><strong>{k}</strong>: {v}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminComparison;
